import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import api from 'services/api';
import { logGroupingDebug } from 'Common/Functions/groupingCommandDebug';
import GroupingPollingManager from 'Common/Components/FileUploadContext/groupingPolling/GroupingPollingManager';
import {
  GROUPING_POLLING_STATES,
  getGroupingCommandTarget,
  getGroupingWorkspaceName,
  isCommandError,
  isGroupingNotTriggeredError,
  isGroupingTimeoutError,
  normalizeGroupingStartTimestamp,
  toApiUtcDate
} from 'Common/Components/FileUploadContext/groupingPolling/utils';
import {
  patchCommand,
  refreshCurrentRouteContext
} from 'Common/Components/FileUploadContext/actions';
import { selectActiveGroupingCommand } from 'store/FileUploaderContext/selector';

const buildInitialGroupingPatch = (command, sinceTimestamp) => ({
  commandType: command.commandType,
  hideFromQueue: command.hideFromQueue === true,
  groupingStartTimestamp: sinceTimestamp,
  groupingSince: toApiUtcDate(new Date(sinceTimestamp)),
  groupingPollingState: GROUPING_POLLING_STATES.POLLING,
  groupingPollingPhase: 'lastGrouping',
  groupingCommandResolved: false,
  awaitingLastGroupingCommand: true,
  finished: false,
  error: false,
  timeOut: false,
  warning: null,
  warnings: null
});

const buildResolvedGroupingPatch = (command, sinceTimestamp, commandId) => ({
  command_id: commandId,
  commandType: command.commandType || 'grouping',
  hideFromQueue: false,
  mockCommandId: null,
  groupingStartTimestamp: sinceTimestamp,
  groupingSince: toApiUtcDate(new Date(sinceTimestamp)),
  groupingPollingState: GROUPING_POLLING_STATES.POLLING,
  groupingPollingPhase: 'command',
  groupingCommandResolved: true,
  awaitingLastGroupingCommand: false,
  finished: false,
  error: false,
  timeOut: false,
  warning: null,
  warnings: null
});

const buildFinishedGroupingPatch = (command, sinceTimestamp, commandId, commandStatus) => {
  const commandFinishedWithError = isCommandError(commandStatus?.duration, commandStatus?.status);

  return {
    command_id: commandId,
    commandType: command.commandType || 'grouping',
    hideFromQueue: false,
    mockCommandId: null,
    groupingStartTimestamp: sinceTimestamp,
    groupingSince: toApiUtcDate(new Date(sinceTimestamp)),
    groupingPollingState: commandFinishedWithError
      ? GROUPING_POLLING_STATES.ERROR
      : GROUPING_POLLING_STATES.FINISHED,
    groupingPollingPhase: 'command',
    groupingCommandResolved: true,
    awaitingLastGroupingCommand: false,
    finished: true,
    error: commandFinishedWithError,
    timeOut: false,
    warning: commandStatus?.warning ?? null,
    warnings: commandStatus?.warnings ?? null
  };
};

const buildNotTriggeredGroupingPatch = (command, sinceTimestamp) => ({
  commandType: command.commandType,
  hideFromQueue: command.hideFromQueue === true,
  groupingStartTimestamp: sinceTimestamp,
  groupingSince: toApiUtcDate(new Date(sinceTimestamp)),
  groupingPollingState: GROUPING_POLLING_STATES.FINISHED,
  groupingPollingPhase: 'command',
  groupingCommandResolved: true,
  awaitingLastGroupingCommand: false,
  finished: true,
  error: false,
  timeOut: false,
  warning: null,
  warnings: null
});

const buildReportFailedGroupingPatch = (command, sinceTimestamp, reportStatus) => ({
  commandType: command.commandType,
  hideFromQueue: command.hideFromQueue === true,
  groupingStartTimestamp: sinceTimestamp,
  groupingSince: toApiUtcDate(new Date(sinceTimestamp)),
  groupingPollingState: GROUPING_POLLING_STATES.ERROR,
  groupingPollingPhase: 'command',
  groupingCommandResolved: true,
  awaitingLastGroupingCommand: false,
  finished: true,
  error: true,
  timeOut: false,
  warning: reportStatus?.warning ?? null,
  warnings: reportStatus?.warnings ?? null,
  errorMessage: reportStatus?.message ?? 'Couldn\'t process your file. Please try again.'
});

const buildErroredGroupingPatch = (command, sinceTimestamp, error) => ({
  commandType: command.commandType || 'grouping',
  groupingStartTimestamp: sinceTimestamp,
  groupingSince: toApiUtcDate(new Date(sinceTimestamp)),
  groupingPollingState: GROUPING_POLLING_STATES.ERROR,
  groupingPollingPhase: 'error',
  groupingCommandResolved: true,
  awaitingLastGroupingCommand: false,
  finished: true,
  error: true,
  timeOut: isGroupingTimeoutError(error),
  warning: error?.message ?? null,
  warnings: null,
  errorMessage: error?.message ?? 'Grouping polling failed.'
});

const defaultState = {
  status: GROUPING_POLLING_STATES.IDLE,
  phase: 'idle',
  since: null,
  commandId: null,
  error: null
};

const useGroupingPolling = () => {
  const dispatch = useDispatch();
  const activeGroupingCommand = useSelector(selectActiveGroupingCommand);
  const activeGroupingSessionKey = activeGroupingCommand?.queuedAt ?? null;
  const [state, setState] = useState(defaultState);
  const managerRef = useRef(null);
  const activeGroupingCommandRef = useRef(activeGroupingCommand);

  activeGroupingCommandRef.current = activeGroupingCommand;

  if (!managerRef.current) {
    managerRef.current = new GroupingPollingManager({
      getLastGroupingCommand: api.workspace.getLastGroupingCommand,
      getCommandStatus: api.workspace.getReportStatus
    });
  }

  useEffect(() => {
    const currentGroupingCommand = activeGroupingCommandRef.current;

    if (!currentGroupingCommand) {
      managerRef.current.cancel();
      setState(defaultState);
      return undefined;
    }

    const target = getGroupingCommandTarget(currentGroupingCommand);
    const sinceTimestamp = normalizeGroupingStartTimestamp(currentGroupingCommand);
    const workspaceName = getGroupingWorkspaceName(currentGroupingCommand);

    if (!workspaceName) {
      const error = new Error('Grouping workspace is missing.');
      dispatch(patchCommand(target, buildErroredGroupingPatch(currentGroupingCommand, sinceTimestamp, error)));
      setState({
        status: GROUPING_POLLING_STATES.ERROR,
        phase: 'error',
        since: toApiUtcDate(new Date(sinceTimestamp)),
        commandId: null,
        error
      });
      return undefined;
    }

    dispatch(patchCommand(target, buildInitialGroupingPatch(currentGroupingCommand, sinceTimestamp)));

    logGroupingDebug('grouping_polling_started', {
      workspaceName,
      commandId: currentGroupingCommand.command_id || null,
      queuedAt: currentGroupingCommand.queuedAt,
      since: toApiUtcDate(new Date(sinceTimestamp))
    });

    managerRef.current.start({
      workspaceName,
      sinceTimestamp,
      onStateChange: (nextState) => {
        setState(nextState);
      },
      onCommandPoll: ({ pollCount, commandId, response }) => {
        logGroupingDebug('grouping_command_poll_result', {
          workspaceName,
          commandId,
          queuedAt: currentGroupingCommand.queuedAt,
          pollCount,
          status: response?.status ?? null,
          duration: response?.duration ?? null,
          commandStatus: response
        });
      },
      onCommandResolved: ({ commandId, since }) => {
        logGroupingDebug('grouping_command_resolved', {
          workspaceName,
          commandId,
          queuedAt: currentGroupingCommand.queuedAt,
          since
        });
        dispatch(patchCommand(target, buildResolvedGroupingPatch(currentGroupingCommand, sinceTimestamp, commandId)));
      },
      onFinished: async ({ commandId, commandStatus, since }) => {
        logGroupingDebug('grouping_polling_finished', {
          workspaceName,
          commandId,
          queuedAt: currentGroupingCommand.queuedAt,
          since,
          status: commandStatus?.status ?? null,
          duration: commandStatus?.duration ?? null
        });
        dispatch(patchCommand(target, buildFinishedGroupingPatch(currentGroupingCommand, sinceTimestamp, commandId, commandStatus)));
        await dispatch(refreshCurrentRouteContext());
      },
      onError: async (error) => {
        if (isGroupingNotTriggeredError(error)) {
          const commandId = currentGroupingCommand.command_id;
          let reportStatus = null;

          if (commandId) {
            try {
              reportStatus = await api.workspace.getReportStatus(commandId);
            } catch (_statusError) {
              reportStatus = null;
            }
          }

          if (reportStatus && isCommandError(reportStatus?.duration, reportStatus?.status)) {
            logGroupingDebug('grouping_polling_not_triggered_report_failed', {
              workspaceName,
              commandId,
              queuedAt: currentGroupingCommand.queuedAt,
              status: reportStatus?.status ?? null,
              duration: reportStatus?.duration ?? null
            });
            dispatch(patchCommand(target, buildReportFailedGroupingPatch(currentGroupingCommand, sinceTimestamp, reportStatus)));
            return;
          }

          logGroupingDebug('grouping_polling_not_triggered', {
            workspaceName,
            commandId: currentGroupingCommand.command_id || null,
            queuedAt: currentGroupingCommand.queuedAt,
            message: error?.message ?? ''
          });
          dispatch(patchCommand(target, buildNotTriggeredGroupingPatch(currentGroupingCommand, sinceTimestamp)));
          return;
        }

        logGroupingDebug('grouping_polling_error', {
          workspaceName,
          commandId: currentGroupingCommand.command_id || null,
          queuedAt: currentGroupingCommand.queuedAt,
          message: error?.message ?? ''
        });
        dispatch(patchCommand(target, buildErroredGroupingPatch(currentGroupingCommand, sinceTimestamp, error)));
      }
    }).catch(() => {});

    return () => {
      managerRef.current.cancel();
    };
  }, [activeGroupingSessionKey, dispatch]);

  return state;
};

export default useGroupingPolling;
